import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Icons, PageHeader, Spinner } from '../../components/ui.tsx';
import { BRAND, BrandIcon, FileTypeIcon } from '../../components/icons.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { SelectBox } from './SelectionBar.tsx';
import { useSelection } from './useSelection.ts';
import { formatBytes, formatDateTime, formatRelative, pluralise } from '../../lib/display.ts';

/**
 * The trash.
 *
 * Deleting has always been soft in this system, but there was no screen, so
 * "moved to the trash — nothing is destroyed" was a message with nowhere to
 * go. Restoring is the primary action and the only one styled as such;
 * destroying is deliberately quieter and always asks.
 */

interface TrashEntry {
  id: string;
  kind: 'file' | 'folder' | 'spreadsheet' | 'document';
  name: string;
  location: string;
  sizeBytes: number | null;
  deletedAt: string;
  contains: number | null;
}

const RESTORE_PATHS: Record<TrashEntry['kind'], (id: string) => string> = {
  file: (id) => `/api/drive/files/${id}/restore`,
  folder: (id) => `/api/drive/folders/${id}/restore`,
  // The controller is mounted at api/sheets, not api/spreadsheets — this
  // pointed at a route that does not exist, so restoring a sheet 404d.
  spreadsheet: (id) => `/api/sheets/${id}/restore`,
  document: (id) => `/api/documents/${id}/restore`,
};

/**
 * A selection, in the shape the bulk endpoints take.
 *
 * One request for the whole lot rather than one per item. The per-item routes
 * are still there and still used by the row menus, where there is only ever
 * one thing to act on.
 */
function selectionOf(entries: TrashEntry[]) {
  const of = (kind: TrashEntry['kind']) =>
    entries.filter((entry) => entry.kind === kind).map((entry) => entry.id);

  return {
    fileIds: of('file'),
    folderIds: of('folder'),
    spreadsheetIds: of('spreadsheet'),
    documentIds: of('document'),
  };
}

const PURGE_PATHS: Record<TrashEntry['kind'], (id: string) => string> = {
  file: (id) => `/api/drive/trash/files/${id}`,
  folder: (id) => `/api/drive/trash/folders/${id}`,
  spreadsheet: (id) => `/api/drive/trash/spreadsheets/${id}`,
  document: (id) => `/api/drive/trash/documents/${id}`,
};

export function TrashPage() {
  const client = useQueryClient();
  const dialog = useDialog();
  const toast = useToast();

  const trash = useQuery({
    queryKey: ['trash'],
    queryFn: () => api.get<TrashEntry[]>('/api/drive/trash'),
  });

  /**
   * Anything that changes the trash also changes the folder listings.
   *
   * Documents were missing from this list, so restoring a letter put it back
   * in the database and nowhere on screen — the Drive kept the listing it had
   * fetched before, and only a reload showed it. The dashboard counts move
   * for the same reason.
   */
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['trash'] });
    void client.invalidateQueries({ queryKey: ['folders'] });
    void client.invalidateQueries({ queryKey: ['files'] });
    void client.invalidateQueries({ queryKey: ['spreadsheets'] });
    void client.invalidateQueries({ queryKey: ['documents'] });
    void client.invalidateQueries({ queryKey: ['breadcrumbs'] });
    void client.invalidateQueries({ queryKey: ['dashboard'] });
    void client.invalidateQueries({ queryKey: ['stats'] });
  };

  const restore = useMutation({
    mutationFn: (entry: TrashEntry) => api.post(RESTORE_PATHS[entry.kind](entry.id)),
    onSuccess: refresh,
  });

  const purge = useMutation({
    mutationFn: (entry: TrashEntry) => api.delete(PURGE_PATHS[entry.kind](entry.id)),
    onSuccess: refresh,
  });

  const purgeMany = useMutation({
    mutationFn: (entries: TrashEntry[]) =>
      api.post<{ removed: number; failed: string[] }>(
        '/api/drive/bulk/purge',
        selectionOf(entries),
      ),
    onSuccess: refresh,
  });

  const restoreMany = useMutation({
    mutationFn: (entries: TrashEntry[]) =>
      api.post<{ restored: number; failed: string[] }>(
        '/api/drive/bulk/restore',
        selectionOf(entries),
      ),
    onSuccess: refresh,
  });

  const emptyAll = useMutation({
    mutationFn: () => api.post<{ removed: number }>('/api/drive/trash/empty'),
    onSuccess: refresh,
  });

  const entries = trash.data ?? [];

  const selection = useSelection();
  const [bulk, setBulk] = useState<'restore' | 'purge' | null>(null);

  /** The selected entries, in the order they appear on screen. */
  const picked = entries.filter((entry) => selection.has(entry.kind, entry.id));

  /**
   * Put back or destroy everything selected.
   *
   * One request per entry rather than a bulk endpoint: restoring can fail for
   * a single entry on its own merits — usually because something has taken the
   * name it left behind — and a batch that stops at the first failure would
   * leave the person with no idea which half went through. Each is attempted,
   * and what failed is counted.
   */
  async function onBulk(action: 'restore' | 'purge') {
    if (action === 'purge') {
      const confirmed = await dialog.confirm({
        title: `Permanently delete ${pluralise(picked.length, 'item')}?`,
        message:
          'This cannot be undone. They are removed from storage entirely, not moved anywhere.',
        confirmLabel: 'Delete permanently',
        tone: 'danger',
      });
      if (!confirmed) return;
    }

    setBulk(action);

    /**
     * One request, not one per item.
     *
     * The loop this replaces sent a request for every entry and refreshed
     * eight queries after each one, so emptying thirty things was around a
     * hundred calls. The rate limiter refused partway through, and what the
     * user saw was a trash that had half emptied and then said it could not
     * be loaded.
     */
    let done = 0;
    const failed: string[] = [];

    try {
      if (action === 'restore') {
        done = (await restoreMany.mutateAsync(picked)).restored;
      } else {
        done = (await purgeMany.mutateAsync(picked)).removed;
      }
      // The server reports what it could not do by id; the names are here.
      const missed = picked.length - done;
      if (missed > 0) failed.push(...picked.slice(done).map((entry) => entry.name));
    } catch {
      failed.push(...picked.map((entry) => entry.name));
    }

    setBulk(null);
    selection.clear();

    if (done > 0) {
      toast.success(
        action === 'restore'
          ? `${pluralise(done, 'item')} restored`
          : `${pluralise(done, 'item')} permanently deleted`,
      );
    }
    if (failed.length > 0) {
      toast.error(
        `Could not ${action === 'restore' ? 'restore' : 'delete'} ${pluralise(failed.length, 'item')}`,
        failed.slice(0, 3).join(', ') + (failed.length > 3 ? '…' : ''),
      );
    }
  }

  async function onRestore(entry: TrashEntry) {
    try {
      await restore.mutateAsync(entry);
      toast.success(`"${entry.name}" restored`, `Back in ${entry.location}.`);
    } catch (caught) {
      // The commonest failure by far: something now occupies the name it left
      // behind. The server says which, so pass its words through.
      toast.error(
        'Could not restore',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function onPurge(entry: TrashEntry) {
    const confirmed = await dialog.confirm({
      title: `Permanently delete "${entry.name}"?`,
      message:
        'This cannot be undone. The file is removed from storage entirely, not moved anywhere.',
      details:
        entry.kind === 'folder' && entry.contains
          ? [`${pluralise(entry.contains, 'item')} inside will be destroyed too`]
          : [],
      confirmLabel: 'Delete permanently',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      await purge.mutateAsync(entry);
      toast.success(`"${entry.name}" permanently deleted`);
    } catch (caught) {
      toast.error(
        'Could not delete',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function onEmpty() {
    const confirmed = await dialog.confirm({
      title: 'Empty the trash?',
      message: 'Everything in the trash is destroyed. This cannot be undone.',
      details: [`${pluralise(entries.length, 'item')} will be permanently deleted`],
      confirmLabel: 'Empty the trash',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      const { removed } = await emptyAll.mutateAsync();
      toast.success('Trash emptied', `${pluralise(removed, 'item')} permanently deleted.`);
    } catch (caught) {
      toast.error(
        'Could not empty the trash',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Trash"
        description="Deleted files, folders and sheets. Nothing here is gone until you say so."
        actions={
          entries.length > 0 ? (
            <Button variant="danger" onClick={() => void onEmpty()} disabled={emptyAll.isPending}>
              {emptyAll.isPending ? <Spinner label="Emptying" /> : 'Empty the trash'}
            </Button>
          ) : undefined
        }
      />

      {selection.count > 0 ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-[var(--blue-300)] bg-blue-50 px-4 py-2.5">
          <span className="text-body-sm font-medium text-blue-700">
            {pluralise(selection.count, 'item')} selected
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button onClick={() => void onBulk('restore')} disabled={bulk !== null}>
              {bulk === 'restore' ? <Spinner label="Restoring" /> : 'Put back'}
            </Button>
            <Button variant="danger" onClick={() => void onBulk('purge')} disabled={bulk !== null}>
              {bulk === 'purge' ? <Spinner label="Deleting" /> : 'Delete permanently'}
            </Button>
            <button
              type="button"
              onClick={selection.clear}
              className="rounded-md px-2 py-1 text-body-sm text-blue-700 transition-fast hover:bg-blue-100"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}

      <div className="mt-4 min-h-0 flex-1 overflow-y-auto pb-6">
        {trash.isLoading ? (
          <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
            <Spinner /> Loading the trash…
          </div>
        ) : trash.isError ? (
          <Alert>Could not load the trash.</Alert>
        ) : entries.length === 0 ? (
          <EmptyState
            title="The trash is empty"
            description="Anything you delete lands here first, and can be put back."
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border-strong bg-surface">
            {/* Scrolls sideways on a phone instead of being clipped. The
                columns here are dates and origins that lose their meaning when
                truncated, so they are kept and moved rather than dropped. */}
            <table className="w-full table-fixed max-md:min-w-[640px]">
              {/* Name absorbs the slack; everything else is fixed and flush
                  against the right edge. */}
              <colgroup>
                <col className="w-10" />
                <col />
                <col className="w-56" />
                <col className="w-24" />
                <col className="w-52" />
                <col className="w-44" />
              </colgroup>
              <thead>
                <tr className="border-b border-border-strong bg-sunken">
                  <th className="pl-4">
                    <SelectBox
                      checked={selection.count > 0 && selection.count === entries.length}
                      indeterminate={selection.count > 0}
                      label="Select everything in the trash"
                      onChange={() =>
                        selection.count === entries.length
                          ? selection.clear()
                          : selection.selectAll(entries)
                      }
                    />
                  </th>
                  <Th>Name</Th>
                  <Th>Was in</Th>
                  <Th align="right">Size</Th>
                  <Th align="right">Deleted</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={`${entry.kind}-${entry.id}`}
                    className={`h-row border-b border-border transition-fast last:border-0 hover:bg-blue-50 ${
                      selection.has(entry.kind, entry.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="pl-4">
                      <SelectBox
                        checked={selection.has(entry.kind, entry.id)}
                        label={`Select ${entry.name}`}
                        onChange={() => selection.toggle(entry.kind, entry.id)}
                      />
                    </td>

                    <td className="px-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="shrink-0" aria-hidden>
                          {entry.kind === 'folder' ? (
                            <BrandIcon src={BRAND.folder} size={20} />
                          ) : entry.kind === 'spreadsheet' ? (
                            <BrandIcon src={BRAND.excel} size={20} />
                          ) : entry.kind === 'document' ? (
                            <BrandIcon src={BRAND.word} size={20} />
                          ) : (
                            <FileTypeIcon name={entry.name} />
                          )}
                        </span>
                        <span className="truncate text-body-sm text-ink" title={entry.name}>
                          {entry.name}
                        </span>
                        {entry.contains ? (
                          <span className="shrink-0 whitespace-nowrap text-micro text-muted">
                            +{pluralise(entry.contains, 'item')}
                          </span>
                        ) : null}
                      </div>
                    </td>

                    <td className="truncate px-4 text-body-sm text-muted" title={entry.location}>
                      {entry.location}
                    </td>

                    <td className="whitespace-nowrap px-4 text-right text-body-sm text-muted">
                      {entry.sizeBytes === null ? '—' : formatBytes(entry.sizeBytes)}
                    </td>

                    <td className="whitespace-nowrap px-4 text-right text-body-sm text-muted">
                      <Tooltip label={formatDateTime(entry.deletedAt)}>
                        <span>{formatRelative(entry.deletedAt)}</span>
                      </Tooltip>
                    </td>

                    <td className="px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          onClick={() => void onRestore(entry)}
                          disabled={restore.isPending}
                          className="h-9 px-3"
                        >
                          Put back
                        </Button>
                        <Tooltip
                          label="Delete permanently"
                          detail="Removes it from storage entirely. This cannot be undone."
                        >
                          <button
                            type="button"
                            aria-label={`Permanently delete ${entry.name}`}
                            onClick={() => void onPurge(entry)}
                            disabled={purge.isPending}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted transition-fast hover:bg-danger-bg hover:text-[var(--danger)]"
                          >
                            <Icons.Trash />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: string; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-4 py-2.5 text-micro font-semibold uppercase tracking-wide text-muted ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
